import { useState, useEffect, useRef } from "react";
// eslint-disable-next-line no-unused-vars
import { createPortal } from "react-dom";
import API from "../services/api";
import {
    getMasters,
    saveMaster,
    deleteMaster
} from "../services/masterService";
import { createJob } from "../services/jobService";
import { useNavigate } from "react-router-dom";
import SearchableSelect from "./SearchableSelect";

/* ─────────────────────────── SVG Icons ─────────────────────────── */
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, transition: "transform .15s", transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const CameraIcon = ({ muted }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={muted ? "#c7cbd1" : "#818cf8"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const SpinnerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 15, height: 15, animation: "spin .8s linear infinite" }}>
    <path d="M12 2a10 10 0 0 1 10 10" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" style={{ width: 10, height: 10 }}>
    <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
  </svg>
);
const CrownIcon = () => <span style={{ marginRight: 3 }}>👑</span>;

const STYLES = `
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

  *, *::before, *::after { box-sizing: border-box; }

  .cjn-wrap { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; font-size: 14px; max-width: 1400px; margin: 0 auto; padding: 0 0 4rem; width: 100%; box-sizing: border-box; }

  .cjn-alert { display: flex; align-items: flex-start; gap: .6rem; background: #fef2f2; border: 1px solid #fee2e2; border-radius: 10px; padding: .75rem 1rem; font-size: .82rem; color: #4b5563; margin-bottom: 1.25rem; line-height: 1.55; width: 100%; box-sizing: border-box; word-break: break-word; }
  .cjn-alert strong { color: #ef4444; font-weight: 700; }

  .cjn-banner { display: flex; align-items: center; gap: .6rem; background: #f0fdf4; border: 1.5px solid #86efac; border-radius: 10px; padding: .75rem 1rem; font-size: .85rem; color: #15803d; font-weight: 600; margin-bottom: 1.25rem; animation: fadeIn .3s ease; width: 100%; box-sizing: border-box; word-break: break-word; }
  .cjn-error { background: #fff5f5; border: 1.5px solid #fecaca; border-radius: 10px; padding: .75rem 1rem; margin-bottom: 1.25rem; animation: fadeIn .3s ease; width: 100%; box-sizing: border-box; }
  .cjn-error .t { display: flex; align-items: center; gap: .5rem; font-size: .85rem; font-weight: 700; color: #dc2626; margin-bottom: .35rem; }
  .cjn-error ul { padding-left: 1.2rem; font-size: .8rem; color: #374151; line-height: 1.7; }

  .cjn-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(15,23,42,.5); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
  .cjn-overlay-box { background: #fff; border-radius: 16px; padding: 2rem 2.5rem; display: flex; flex-direction: column; align-items: center; gap: 1rem; box-shadow: 0 20px 60px rgba(0,0,0,.2); min-width: 220px; text-align: center; }
  .cjn-overlay-spin { width: 44px; height: 44px; border: 3.5px solid #e2e8f0; border-top-color: #4f46e5; border-radius: 50%; animation: spin .7s linear infinite; }
  .cjn-overlay-title { font-size: .95rem; font-weight: 700; color: #0f172a; }
  .cjn-overlay-sub { font-size: .78rem; color: #64748b; }

  .cjn-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; margin-bottom: 1.25rem; width: 100%; box-sizing: border-box; overflow: visible; box-shadow: 0 1px 3px 0 rgba(0,0,0,0.03); transition: border-color 0.15s ease, box-shadow 0.15s ease; position: relative; }
  .cjn-card-hd { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; gap: .75rem; flex-wrap: wrap; width: 100%; box-sizing: border-box; background: #fafafa; border-top-left-radius: 14px; border-top-right-radius: 14px; }
  .cjn-card-hd h2 { font-size: .98rem; font-weight: 700; color: #1e293b; margin: 0; }
  .cjn-card-hd .sub { font-size: .76rem; color: #64748b; margin-top: 2px; }
  .cjn-card-body { padding: 20px; width: 100%; box-sizing: border-box; }

  .cjn-jobno-pill { font-size: .75rem; background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe; border-radius: 8px; padding: 4px 10px 4px 12px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
  .cjn-jobno-pill input { font-weight: 700; background: transparent; color: #3730a3; outline: none; border: none; border-bottom: 1px solid transparent; width: 120px; }
  .cjn-jobno-pill input:focus { border-bottom-color: #6366f1; }

  .cjn-grid3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; width: 100%; }
  .cjn-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; width: 100%; }
  @media (max-width: 900px) { .cjn-grid3 { grid-template-columns: 1fr !important; } .cjn-grid2 { grid-template-columns: 1fr !important; } }

  .cjn-fg { display: flex; flex-direction: column; gap: .35rem; width: 100%; position: relative; }
  .cjn-label { font-size: .68rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #475569; }
  .cjn-label .req { color: #ef4444; margin-left: 2px; }

  .cjn-input, .cjn-select, .cjn-textarea {
    width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px;
    font-size: .875rem; color: #0f172a; background: #ffffff; outline: none;
    font-family: inherit; transition: all .15s ease-in-out; box-sizing: border-box;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  }
  .cjn-input:hover, .cjn-select:hover, .cjn-textarea:hover { border-color: #cbd5e1; }
  .cjn-input:focus, .cjn-select:focus, .cjn-textarea:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.12); }
  .cjn-input.err, .cjn-select.err, .cjn-tsel.err, .cjn-tinp.err { border-color: #f87171 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.08); }
  .cjn-select { appearance: none; cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 10px center; padding-right: 30px; }
  .cjn-select:disabled { background-color: #f8fafc; color: #94a3b8; cursor: not-allowed; }
  .cjn-textarea { resize: none; min-height: 48px; }
  .cjn-err-msg { font-size: .7rem; color: #dc2626; font-weight: 500; }
  .cjn-help { font-size: .72rem; color: #64748b; margin-top: 2px; }

  .cjn-tsel, .cjn-tinp { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 7px 10px; font-size: .84rem; color: #0f172a; background: #fff; outline: none; font-family: inherit; box-sizing: border-box; transition: all 0.15s ease; }
  .cjn-tsel { appearance: none; cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 10px center; padding-right: 26px; }
  .cjn-tsel:focus, .cjn-tinp:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.1); }
  textarea.cjn-tinp { resize: vertical; min-height: 52px; }

  /* Staff assignment */
  .cjn-staffbox { border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px; background: #fff; width: 100%; box-sizing: border-box; position: relative; }
  .cjn-staffbox.err { border-color: #f87171; box-shadow: 0 0 0 3px rgba(239,68,68,.08); }
  .cjn-tags { display: flex; flex-wrap: wrap; gap: 6px; min-height: 26px; margin-bottom: 6px; }
  .cjn-tag { display: inline-flex; align-items: center; gap: 4px; background: #eef2ff; color: #4338ca; border-radius: 6px; padding: 3px 8px; font-size: .74rem; font-weight: 600; }
  .cjn-tag.leader { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
  .cjn-tag .x { cursor: pointer; color: #6366f1; font-weight: 700; padding: 0 2px; border-radius: 3px; }
  .cjn-tag.leader .x { color: #92400e; }
  .cjn-tag .x:hover { background: rgba(0,0,0,.08); }

  .cjn-qwrap { position: relative; display: inline-block; }
  .cjn-linkbtn { background: none; border: none; cursor: pointer; font-size: .76rem; font-weight: 600; color: #6366f1; padding: 0; }
  .cjn-linkbtn:hover { color: #4338ca; }
  .cjn-panel { position: absolute; top: calc(100% + 6px); left: 0; z-index: 1000; width: 270px; background: #fff; border: 1px solid #cbd5e1; border-radius: 12px; box-shadow: 0 16px 36px -6px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.06); padding: 12px; animation: fadeIn .12s ease; }
  .cjn-panel-title { font-size: .68rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 8px; }
  .cjn-panel-list { max-height: 125px; overflow-y: auto; margin-bottom: 8px; scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
  .cjn-panel-list::-webkit-scrollbar { width: 5px; }
  .cjn-panel-list::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
  .cjn-panel-item { display: flex; align-items: center; justify-content: space-between; font-size: .78rem; color: #334155; padding: 6px 8px; border-radius: 6px; }
  .cjn-panel-item:hover { background: #f8fafc; }
  .cjn-panel-item .del { color: #dc2626; cursor: pointer; font-size: .8rem; padding: 0 4px; border-radius: 4px; }
  .cjn-panel-item .del:hover { background: #fee2e2; }
  .cjn-panel-empty { font-size: .74rem; color: #94a3b8; padding: 6px; text-align: center; }
  .cjn-panel-addrow { display: flex; gap: 6px; }
  .cjn-panel-addrow input { flex: 1; font-size: .76rem; border: 1px solid #e2e8f0; border-radius: 6px; padding: 5px 8px; outline: none; }
  .cjn-panel-addrow input:focus { border-color: #818cf8; }
  .cjn-panel-addrow button { font-size: .72rem; font-weight: 700; background: #fef08a; color: #713f12; border: none; border-radius: 6px; padding: 5px 10px; cursor: pointer; }
  .cjn-panel-addrow button:hover { background: #fde047; }

  .cjn-quickstrip { padding: 14px 20px; border-top: 1px solid #f1f5f9; background: #f8fafc; border-radius: 0 0 14px 14px; width: 100%; box-sizing: border-box; }
  .cjn-quickstrip .lbl { font-size: .72rem; color: #64748b; font-weight: 600; margin-bottom: 8px; }
  .cjn-qbtn-row { display: flex; flex-wrap: wrap; gap: 8px; width: 100%; }
  .cjn-qbtn { display: inline-flex; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; font-size: .75rem; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
  .cjn-qbtn .l { padding: 6px 12px; background: #fff; color: #334155; font-weight: 500; }
  .cjn-qbtn .a { padding: 6px 12px; background: #fef9c3; color: #854d0e; font-weight: 700; cursor: pointer; border-left: 1px solid #e2e8f0; transition: background 0.15s ease; }
  .cjn-qbtn .a:hover { background: #fef08a; }

  /* Article cards & Grid */
  .cjn-artfields { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; width: 100%; align-items: start; margin-bottom: 14px; }
  .cjn-artcard { border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; overflow: hidden; margin-bottom: 14px; width: 100%; box-sizing: border-box; box-shadow: 0 1px 3px rgba(0,0,0,0.03); transition: border-color 0.15s ease, box-shadow 0.15s ease; }
  .cjn-artcard:hover { border-color: #cbd5e1; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.04); }
  .cjn-artcard-hd { background: #f8fafc; padding: 12px 18px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e2e8f0; cursor: pointer; user-select: none; gap: 12px; flex-wrap: wrap; transition: background 0.15s ease; }
  .cjn-artcard-hd:hover { background: #f1f5f9; }
  .cjn-artcard-num { width: 26px; height: 26px; border-radius: 50%; background: #e0e7ff; color: #4338ca; display: flex; align-items: center; justify-content: center; font-size: .75rem; font-weight: 700; flex-shrink: 0; }
  .cjn-artcard-title { font-size: .88rem; font-weight: 600; color: #1e293b; }
  .cjn-artcard-loc { font-size: .75rem; color: #64748b; margin-left: 8px; }
  .cjn-artcard-badge { font-size: .72rem; background: #e0e7ff; color: #4338ca; border-radius: 12px; padding: 3px 10px; font-weight: 600; white-space: nowrap; }
  .cjn-artcard-badge.empty { background: #f1f5f9; color: #64748b; font-weight: 500; }
  .cjn-artcard-badge.errbadge { background: #fee2e2; color: #dc2626; }
  .cjn-iconbtn { background: none; border: none; color: #94a3b8; cursor: pointer; padding: 6px; display: flex; align-items: center; border-radius: 6px; transition: color .12s, background .12s; }
  .cjn-iconbtn:hover { color: #ef4444; background: #fee2e2; }
  .cjn-artcard-body { padding: 18px; width: 100%; box-sizing: border-box; }

  .cjn-matsub { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; margin-top: 14px; width: 100%; box-sizing: border-box; }
  .cjn-matsub-hd { font-size: .72rem; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px; }
  .cjn-matsub-hd .addlink { display: inline-flex; align-items: center; gap: 5px; color: #4f46e5; cursor: pointer; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 6px; padding: 4px 10px; font-weight: 600; font-size: .74rem; transition: background 0.15s ease; }
  .cjn-matsub-hd .addlink:hover { background: #e0e7ff; color: #3730a3; }
  .cjn-matrow { display: grid; grid-template-columns: 1fr 90px 90px 34px; gap: 10px; align-items: start; margin-bottom: 8px; width: 100%; }
  .cjn-matrow:last-child { margin-bottom: 0; }
  .cjn-matcol-lbl { font-size: .68rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .04em; }
  .cjn-matempty { font-size: .78rem; color: #94a3b8; padding: 12px; text-align: center; background: #ffffff; border: 1px dashed #e2e8f0; border-radius: 8px; }

  .cjn-dashedbtn { width: 100%; text-align: center; font-size: .85rem; color: #4f46e5; border: 1.5px dashed #c7d2fe; border-radius: 10px; padding: 12px; background: #ffffff; cursor: pointer; font-weight: 600; font-family: inherit; transition: all .15s ease; box-sizing: border-box; display: flex; align-items: center; justify-content: center; gap: 6px; }
  .cjn-dashedbtn:hover { background: #f5f3ff; border-color: #818cf8; }

  .cjn-addcardbtn { display: inline-flex; align-items: center; gap: 6px; font-size: .8rem; font-weight: 600; color: #4338ca; border: 1px solid #c7d2fe; background: #eef2ff; border-radius: 8px; padding: 7px 14px; cursor: pointer; font-family: inherit; transition: background 0.15s ease; }
  .cjn-addcardbtn:hover { background: #e0e7ff; }

  /* Tables (materials issued / photos) */
  .cjn-table-wrap { overflow-x: auto; width: 100%; -webkit-overflow-scrolling: touch; }
  .cjn-table { width: 100%; border-collapse: collapse; font-size: .8rem; min-width: 500px; }
  .cjn-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-size: .68rem; font-weight: 700; color: #64748b; letter-spacing: .04em; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
  .cjn-table.warn th { background: #fffbeb; color: #92400e; border-bottom-color: #fde68a; }
  .cjn-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  .cjn-table tr:last-child td { border-bottom: none; }
  .cjn-table tr:hover td { background: #fafafa; }

  /* Modernized Photo upload box */
  .cjn-photobox {
    width: 100%; height: 38px; border: 1.5px dashed #cbd5e1; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    cursor: pointer; background: #f8fafc; transition: all 0.15s ease;
    overflow: hidden; position: relative; box-sizing: border-box;
  }
  .cjn-photobox:hover { border-color: #6366f1; background: #f5f3ff; color: #4f46e5; }
  .cjn-photobox.has-image { height: 74px; border-style: solid; border-color: #e2e8f0; background: #fff; padding: 0; }
  .cjn-photobox img { width: 100%; height: 100%; object-fit: cover; }
  .cjn-photobox.err { border-color: #f87171; background: #fef2f2; }

  .cjn-photo-overlay {
    position: absolute; inset: 0; background: rgba(15,23,42,0.45); opacity: 0;
    transition: opacity 0.15s ease; display: flex; align-items: center; justify-content: center; gap: 10px; z-index: 2;
  }
  .cjn-photobox:hover .cjn-photo-overlay { opacity: 1; }
  
  .cjn-photo-btn {
    width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: #fff; cursor: pointer; transition: transform 0.12s ease; border: none; outline: none;
  }
  .cjn-photo-btn.view { background: #3b82f6; }
  .cjn-photo-btn.view:hover { background: #2563eb; transform: scale(1.1); }
  .cjn-photo-btn.remove { background: #ef4444; }
  .cjn-photo-btn.remove:hover { background: #dc2626; transform: scale(1.1); }

  .cjn-photocell { display: flex; flex-direction: column; align-items: center; gap: 4px; }
  .cjn-photo-err-msg { font-size: .62rem; color: #dc2626; text-align: center; max-width: 80px; }


  .cjn-footer { display: flex; align-items: center; justify-content: flex-end; gap: 10px; padding-top: 8px; width: 100%; }
  .cjn-btn-ghost { padding: 9px 18px; font-size: .85rem; font-weight: 600; color: #4b5563; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; font-family: inherit; }
  .cjn-btn-ghost:hover { background: #f9fafb; }
  .cjn-btn-ghost:disabled { opacity: .5; cursor: not-allowed; }
  .cjn-btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 9px 22px; font-size: .85rem; font-weight: 700; color: #fff; background: #5b4cf5; border: none; border-radius: 8px; cursor: pointer; font-family: inherit; }
  .cjn-btn-primary:hover:not(:disabled) { background: #4338ca; }
  .cjn-btn-primary:disabled { background: #a5b4fc; cursor: not-allowed; }

  @media (max-width: 640px) {
    .cjn-wrap { padding: 0 0 2rem; width: 100%; }
    .cjn-alert { font-size: .78rem; padding: 10px 12px; flex-direction: column; }
    .cjn-card-hd { flex-direction: column; align-items: flex-start; gap: 8px; padding: 12px; }
    .cjn-card-body { padding: 12px; }
    .cjn-jobno-pill { width: 100%; justify-content: space-between; }
    .cjn-jobno-pill input { width: auto; flex: 1; text-align: right; }
    
    .cjn-artfields {
      grid-template-columns: 1fr !important;
      gap: 10px;
    }
    
    .cjn-matrow {
      grid-template-columns: 1fr 1fr !important;
      gap: 6px;
      padding: 8px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      margin-bottom: 8px;
    }

    .cjn-quickstrip { padding: 10px 12px 14px; }
    .cjn-qbtn-row { flex-direction: column; width: 100%; }
    .cjn-qbtn { width: 100%; display: flex; }
    .cjn-qbtn .l { flex: 1; }
    .cjn-panel {
      width: calc(100vw - 32px) !important;
      max-width: 320px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
    }

    .cjn-footer {
      flex-direction: column-reverse;
      width: 100%;
    }
    .cjn-btn-ghost, .cjn-btn-primary {
      width: 100%;
      justify-content: center;
    }
  }

  /* ==========================
   Searchable Dropdown
========================== */

.cjn-dropdown{
    position:relative;
    width:100%;
}

.cjn-dropdown-trigger{
    cursor:pointer;
    display:flex;
    align-items:center;
    justify-content:space-between;
    background:#fff;
    min-height:38px;
}

.cjn-dropdown-trigger::after{
    content:"▼";
    font-size:10px;
    color:#6b7280;
}

.cjn-dropdown-menu{
    position:absolute;
    top:100%;
    left:0;
    right:0;
    z-index:1000;

    margin-top:4px;
    background:#fff;
    border:1px solid #d1d5db;
    border-radius:8px;

    box-shadow:0 8px 24px rgba(0,0,0,.12);

    padding:8px;

    max-height:160px;
    overflow-y:auto;
}

.cjn-dropdown-menu input{
    margin-bottom:8px;
}

.cjn-dropdown-item{
    padding:8px 10px;
    border-radius:6px;
    cursor:pointer;
    transition:.2s;
}

.cjn-dropdown-item:hover{
    background:#f3f4f6;
}

.cjn-dropdown-item:not(:last-child){
    margin-bottom:2px;
}

.cjn-photo-preview{
    width:100%;
    height:100%;
    object-fit:cover;
    border-radius:8px;
}

.cjn-photo-remove{
    position:absolute;
    top:4px;
    right:4px;

    width:22px;
    height:22px;

    border-radius:50%;
    background:#ef4444;
    color:#fff;

    display:flex;
    align-items:center;
    justify-content:center;

    cursor:pointer;
    z-index:2;
}

.cjn-photo-remove:hover{
    background:#dc2626;
}

  /* ── Master Management Modal ── */
  .cjn-modal-backdrop {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(15, 23, 42, 0.55); backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center; padding: 16px;
    animation: fadeIn .15s ease;
  }
  .cjn-modal-box {
    background: #ffffff; border-radius: 16px;
    width: 100%; max-width: 500px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
    overflow: hidden; display: flex; flex-direction: column;
    max-height: 85vh; animation: slideUp .2s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(.98); } to { opacity:1; transform:translateY(0) scale(1); } }
  .cjn-modal-hd {
    padding: 16px 20px; border-bottom: 1px solid #f1f5f9;
    display: flex; align-items: center; justify-content: space-between;
    background: #f8fafc;
  }
  .cjn-modal-hd h3 { margin: 0; font-size: 1.05rem; font-weight: 700; color: #0f172a; }
  .cjn-modal-hd p { margin: 2px 0 0; font-size: 0.78rem; color: #64748b; }
  .cjn-modal-close {
    border: none; background: #f1f5f9; color: #64748b;
    width: 32px; height: 32px; border-radius: 8px; font-size: 1rem;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    font-weight: 700; transition: background 0.12s;
  }
  .cjn-modal-close:hover { background: #e2e8f0; color: #0f172a; }
  .cjn-modal-body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; }
  .cjn-modal-addrow { display: flex; gap: 8px; }
  .cjn-modal-addbtn {
    background: #5b4cf5; color: #ffffff; border: none; border-radius: 8px;
    padding: 8px 14px; font-size: 0.82rem; font-weight: 700; cursor: pointer;
    white-space: nowrap; transition: background 0.12s;
  }
  .cjn-modal-addbtn:hover:not(:disabled) { background: #4338ca; }
  .cjn-modal-addbtn:disabled { opacity: 0.5; cursor: not-allowed; }
  .cjn-modal-list {
    max-height: 260px; overflow-y: auto; border: 1px solid #e2e8f0;
    border-radius: 10px; padding: 6px; background: #fafafa;
  }
  .cjn-modal-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px; background: #ffffff; border: 1px solid #f1f5f9;
    border-radius: 8px; margin-bottom: 4px; font-size: 0.85rem; color: #1e293b; font-weight: 500;
  }
  .cjn-modal-item:last-child { margin-bottom: 0; }
  .cjn-modal-delbtn {
    border: 1px solid #fecaca; background: #fef2f2; color: #dc2626;
    border-radius: 6px; padding: 4px 8px; font-size: 0.74rem; font-weight: 600;
    cursor: pointer; transition: background 0.12s;
  }
  .cjn-modal-delbtn:hover { background: #fee2e2; }
  .cjn-modal-empty { padding: 20px; text-align: center; font-size: 0.8rem; color: #94a3b8; }
  .cjn-modal-ft { padding: 12px 20px; border-top: 1px solid #f1f5f9; background: #f8fafc; display: flex; justify-content: flex-end; }

.cjn-photobox{
    position:relative;
    overflow:hidden;
}

.cjn-photo-preview{
    width:100%;
    height:100%;
    object-fit:cover;
}

.cjn-photo-view{
    position:absolute;
    top:4px;
    left:4px;

    width:22px;
    height:22px;

    border-radius:50%;
    background:#2563eb;
    color:#fff;

    display:flex;
    justify-content:center;
    align-items:center;

    cursor:pointer;
    font-size:12px;
}

.cjn-photo-remove{
    position:absolute;
    top:4px;
    right:4px;
}
.cjn-photo-preview{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
}
.cjn-photo-view{
    position:absolute;
    top:4px;
    left:4px;

    width:22px;
    height:22px;

    border-radius:50%;
    background:#2563eb;
    color:#fff;

    display:flex;
    align-items:center;
    justify-content:center;

    cursor:pointer;
    z-index:10;
}

/*==================================
 IMAGE PREVIEW
===================================*/

.cjn-preview-overlay{
    position:fixed;
    top:0;
    left:0;
    right:0;
    bottom:0;

    background:rgba(0,0,0,.85);

    display:flex;
    justify-content:center;
    align-items:center;

    z-index:999999999;
}

.cjn-preview-container{
    position:relative;

    max-width:90vw;
    max-height:90vh;
}

.cjn-preview-image{
    display:block;

    max-width:90vw;
    max-height:90vh;

    object-fit:contain;

    border-radius:10px;
    background:#fff;
}

.cjn-preview-close{
    position:absolute;

    top:-15px;
    right:-15px;

    width:38px;
    height:38px;

    border:none;
    border-radius:50%;

    background:#ef4444;
    color:#fff;

    font-size:18px;

    cursor:pointer;

    display:flex;
    align-items:center;
    justify-content:center;
}

`;

/* ─────────────────────────── Static option lists ─────────────────────────── */
const ARTICLE_TYPE_OPTIONS = ["Direct Wrap", "Overlay", "Partial Wrap"];
const JOB_STATUS_OPTIONS = ["Ongoing", "Pending", "Hold", "Completed", "Revisit"];
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB (matches Laravel `max:5120` in KB)

const DEFAULT_QDB = {
    article: [
        { id: 1, name: "Vinyl Wrap" },
        { id: 2, name: "Architectural Film" },
        { id: 3, name: "Wall Coverings" },
        { id: 4, name: "Solar Film" },
        { id: 5, name: "Glass Tint" },
    ],
    jobtitle: [
        { id: 1, name: "Interior Wrapping" },
        { id: 2, name: "Exterior Wrapping" },
        { id: 3, name: "Commercial Installation" },
        { id: 4, name: "Residential Renovation" },
    ],
    material: [
        { id: 1, name: "Vinyl Wrap Rolls" },
        { id: 2, name: "Paint" },
        { id: 3, name: "LVT / SPC Flooring" },
        { id: 4, name: "Carpet Roll" },
        { id: 5, name: "Epoxy Resin" },
        { id: 6, name: "Micro-Cement" },
        { id: 7, name: "Stone Veneer" },
        { id: 8, name: "ACP Sheet" },
        { id: 9, name: "Décor Panel" },
        { id: 10, name: "Adhesive / Primer" },
        { id: 11, name: "Masking Tape" },
        { id: 12, name: "Grout / Sealant" },
    ],
};

/* ─────────────────────────── Helpers ─────────────────────────── */
const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getLoggedUser = () => {
  try { return JSON.parse(localStorage.getItem("user")) || null; }
  catch { return null; }
};

const genJobNumber = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const seq = String(Math.floor(Math.random() * 999) + 1).padStart(3, "0");
  return `JOB-${ymd}-${seq}`;
};

/** Validates a File against the Laravel `image|mimes:jpg,jpeg,png|max:5120` rule. Returns an error string or null. */
const validateImageFile = (file) => {
  if (!file) return null;
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "Only JPG, JPEG, or PNG images are allowed.";
  if (file.size > MAX_IMAGE_BYTES) return "Image must not exceed 5 MB.";
  return null;
};

const emptyArticle = () => ({
  id: uid(),
  article: "",
  location: "",
  type: "",
    assignedStaff: [],
  estTime: "",
  initialPhoto: null,
  initialPhotoPreview: null,
  description: "",
  teamLeaderRemarks: "",
  supervisorRemarks: "",
  materials: [],
  open: true,
});

const emptyMaterialIssued = () => ({
  id: uid(), material: "", issuedQty: "", used: "", unit: "", returnQty: "", remarks: "",
});

const emptyPhotoRow = () => ({
  id: uid(), description: "", jobCardPhoto: null, jobCardPreview: null,
  beforePhoto: null, beforePreview: null, afterPhotos: [], afterPreviews: [],
  selfRemark: "", teamLeaderRemarks: "", supervisorRemarks: "",
});

/* ── Reusable Multi-Select Staff Dropdown with Portal ── */
// eslint-disable-next-line no-unused-vars
export function MultiSelectStaffDropdown({
  assignedStaff = [],
  assignedStaffIds = [],
  getStaffName,
  onToggleAllTeam,
  onToggleStaff,
  error = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [coords, setCoords] = useState({ top: 0, bottom: "auto", left: 0, width: 0, maxHeight: 280 });
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Prefer downwards; only open upwards if very tight below (<140px) and there's more room above
      const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
      const width = Math.max(rect.width, 200);
      let left = rect.left;
      if (left + width > window.innerWidth - 10) {
        left = Math.max(10, window.innerWidth - width - 10);
      }
      if (left < 10) left = 10;

      if (openUp) {
        setCoords({
          top: "auto",
          bottom: window.innerHeight - rect.top + 4,
          left,
          width,
          maxHeight: Math.min(280, Math.max(120, spaceAbove - 16)),
        });
      } else {
        setCoords({
          top: rect.bottom + 4,
          bottom: "auto",
          left,
          width,
          maxHeight: Math.min(280, Math.max(120, spaceBelow - 16)),
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      if (searchInputRef.current) {
        searchInputRef.current.focus({ preventScroll: true });
      }
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const isAllSelected =
    assignedStaffIds.length > 0 &&
    assignedStaffIds.every((id) => assignedStaff.includes(String(id)));

  const filteredStaffIds = assignedStaffIds.filter((id) =>
    getStaffName(id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasSelected = Array.isArray(assignedStaff) && assignedStaff.length > 0;
  const displayLabel = hasSelected
    ? assignedStaff.map((id) => getStaffName(id)).join(", ")
    : "— Select staff —";

  return (
    <div
      ref={containerRef}
      className="cjn-dropdown-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <div
        className={`cjn-input ${error ? "err" : ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          cursor: "pointer",
          background: "#ffffff",
          color: hasSelected ? "#0f172a" : "#94a3b8",
          borderColor: error ? "#f87171" : isOpen ? "#6366f1" : "#e2e8f0",
          boxShadow: isOpen
            ? "0 0 0 3px rgba(99, 102, 241, 0.12)"
            : "0 1px 2px rgba(0,0,0,0.02)",
          borderRadius: "8px",
          padding: "8px 12px",
          minHeight: "38px",
          transition: "all 0.15s ease",
          userSelect: "none",
        }}
        onClick={() => {
          if (!isOpen) updatePosition();
          setIsOpen((prev) => !prev);
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "0.86rem",
            fontWeight: hasSelected ? 500 : 400,
            flex: 1,
          }}
        >
          {displayLabel}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            width: 14,
            height: 14,
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            color: "#6b7280",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="cjn-dropdown-menu"
            style={{
              position: "fixed",
              top: coords.top !== "auto" ? `${coords.top}px` : "auto",
              bottom: coords.bottom !== "auto" ? `${coords.bottom}px` : "auto",
              left: `${coords.left}px`,
              width: `${coords.width}px`,
              maxHeight: `${coords.maxHeight}px`,
              zIndex: 999999,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              boxShadow:
                "0 12px 28px -4px rgba(15, 23, 42, 0.15), 0 4px 12px -2px rgba(15, 23, 42, 0.08)",
              padding: "8px",
              display: "flex",
              flexDirection: "column",
              animation: "fadeIn 0.15s ease",
              boxSizing: "border-box",
            }}
          >
            {/* Search staff header */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                marginBottom: "6px",
                flexShrink: 0,
              }}
            >
              <span style={{ position: "absolute", left: "10px", pointerEvents: "none" }}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: 14, height: 14, color: "#9ca3af" }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className="cjn-input"
                style={{
                  padding: "6px 10px 6px 30px",
                  fontSize: "0.82rem",
                  width: "100%",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  outline: "none",
                  background: "#f8fafc",
                }}
                placeholder="Search staff member..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div style={{ flex: 1, overflowY: "auto", minHeight: 0, paddingRight: "2px", paddingBottom: "8px" }}>
              {/* All Team toggle */}
              <div
                className="cjn-dropdown-item"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAllTeam();
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.84rem",
                  color: "#3730a3",
                  background: isAllSelected ? "#eef2ff" : "transparent",
                  borderBottom: "1px solid #f1f5f9",
                  marginBottom: "4px",
                }}
              >
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  readOnly
                  style={{ accentColor: "#4f46e5", cursor: "pointer" }}
                />
                <span>All Team</span>
              </div>

              {/* Individual Staff Members */}
              {filteredStaffIds.length === 0 ? (
                <div
                  style={{
                    padding: "12px 8px",
                    fontSize: "0.82rem",
                    color: "#94a3b8",
                    textAlign: "center",
                  }}
                >
                  No staff members found
                </div>
              ) : (
                filteredStaffIds.map((id) => {
                  const staffId = String(id);
                  const isSelected = assignedStaff.includes(staffId);
                  return (
                    <div
                      key={staffId}
                      className="cjn-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStaff(staffId);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.84rem",
                        color: isSelected ? "#4338ca" : "#334155",
                        background: isSelected ? "#f5f3ff" : "transparent",
                        fontWeight: isSelected ? 600 : 400,
                        marginBottom: "2px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        style={{ accentColor: "#4f46e5", cursor: "pointer" }}
                      />
                      <span>{getStaffName(staffId)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ═══════════════════════════ Component ═══════════════════════════ */
function CreateJobFormNew1({ editJobId = null, onSaveSuccess = null, onCloseEdit = null }) {
  const navigate = useNavigate();
  const loggedUser = getLoggedUser();
  const userRole = String(loggedUser?.role || "").toLowerCase();
  const isSupervisor = userRole === "supervisor";

  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [serverErrors, setServerErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [photoErrors, setPhotoErrors] = useState({}); // keyed by `${rowId}_${field}` or `article_${id}`

  /* ── Job number ── */
  const [jobNumber, setJobNumber] = useState("");
  useEffect(() => { setJobNumber(genJobNumber()); }, []);

  /* ── Basic details ── */
  const [clientName, setClientName] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobStatus, setJobStatus] = useState("Ongoing");

  /* ── Staff list (from backend, admin/supervisor view) ── */
  const [staffList, setStaffList] = useState([]);
  const [staffSearch, setStaffSearch] = useState("");
  const [filteredStaff, setFilteredStaff] = useState([]);

  // useEffect(() => {
  //   API.get("/users/staff-supervisors")
  //     .then((res) => {
  //       if (res.data.success) {
  //         setStaffList(res.data.data);
  //         setFilteredStaff(res.data.data);
  //       }
  //     })
  //     .catch((err) => console.log("Error:", err));
  // }, []);
  const [qdb, setQdb] = useState(DEFAULT_QDB);

  const [activeQPanel, setActiveQPanel] = useState(null); // 'article' | 'jobtitle' | 'material' | null
  const [qInput, setQInput] = useState("");

  const [qSearch, setQSearch] = useState({
      article: "",
      jobtitle: "",
      material: "",
  });

  // eslint-disable-next-line no-unused-vars
  const [filteredQdb, setFilteredQdb] = useState({
      article: DEFAULT_QDB.article.slice(0, 5),
      jobtitle: DEFAULT_QDB.jobtitle.slice(0, 5),
      material: DEFAULT_QDB.material.slice(0, 5),
  });

  const [masterLoading, setMasterLoading] = useState({
      article: false,
      jobtitle: false,
      material: false,
  });
    useEffect(() => {
        loadStaff();
        loadMasters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!editJobId) return;
        API.get(`/jobdetails/${editJobId}`)
          .then((res) => {
            const d = res.data;
            if (d.success) {
              const j = d.data.job || d.data;
              if (j) {
                if (j.job_no) setJobNumber(j.job_no);
                if (j.client_name) setClientName(j.client_name);
                if (j.location) setLocation(j.location);
                if (j.address) setAddress(j.address);
                if (j.work_type || j.job_title) setJobTitle(j.work_type || j.job_title);
                const initialTlId = d.data.team_leader?.id || d.data.team_leader_id || j.team_leader_id || (typeof d.data.team_leader === "object" ? d.data.team_leader?.id : d.data.team_leader);
                if (initialTlId) setTeamLeaderId(String(initialTlId));

                const extractedNames = {};
                const parseStaffItem = (st) => {
                  if (!st) return "";
                  if (typeof st === "object") {
                    const id = String(st.id || st.user_id || st.value || "");
                    const name = st.name || st.full_name || st.username || st.label || "";
                    if (id && name) extractedNames[id] = name;
                    return id.replace(/[[\\]"']/g, "").trim() || (name ? name : "");
                  }
                  return String(st).replace(/[[\\]"']/g, "").trim();
                };

                const rawJobStaff = d.data.assigned_staff || j.staff_id;
                let jobStaffItems = rawJobStaff;
                if (typeof rawJobStaff === "string" && (rawJobStaff.startsWith("[") || rawJobStaff.startsWith("{"))) {
                  try { jobStaffItems = JSON.parse(rawJobStaff); } catch(e) {}
                }
                if (!Array.isArray(jobStaffItems)) {
                  jobStaffItems = jobStaffItems ? [jobStaffItems] : [];
                }
                const sIds = jobStaffItems.map(parseStaffItem).filter(Boolean);

                const arts = d.data.articles || d.data.activities || [];
                const articleStaffIds = [];

                if (arts.length > 0) {
                  const parsedArticles = arts.map((a) => {
                    const rawStaff = a.assigned_staff ?? a.assignedStaff;
                    let staffItems = rawStaff;
                    if (typeof rawStaff === "string" && (rawStaff.startsWith("[") || rawStaff.startsWith("{"))) {
                      try { staffItems = JSON.parse(rawStaff); } catch(e) {}
                    }
                    if (!Array.isArray(staffItems)) {
                      staffItems = staffItems !== null && staffItems !== undefined ? [staffItems] : [];
                    }
                    const aStaffIds = staffItems.map(parseStaffItem).filter(Boolean);
                    aStaffIds.forEach((id) => articleStaffIds.push(id));

                    return {
                      id: a.id || uid(),
                      article: a.article || a.name || "",
                      location: a.location || "",
                      type: a.type || a.job_type || "",
                      assignedStaff: aStaffIds,
                      estTime: a.est_time || "",
                      initialPhoto: null,
                      initialPhotoPreview: a.initial_photo_url || null,
                      description: a.description || "",
                      teamLeaderRemarks: a.team_leader_remarks || "",
                      supervisorRemarks: a.supervisor_remarks || "",
                      materials: (a.materials || []).map((m) => ({
                        id: uid(),
                        material: typeof m === "object" && m !== null
                          ? (m.material || m.material_name || m.name || m.code || m.material_code || m.material_id || "")
                          : String(m || ""),
                        qty: m.quantity ?? m.qty ?? 1,
                        unit: m.unit || "pcs",
                      })),
                      open: true,
                    };
                  });
                  setArticles(parsedArticles);
                }

                setStaffNameMap((prev) => ({ ...extractedNames, ...prev }));

                const allStaffIds = Array.from(new Set([...sIds, ...articleStaffIds, ...(initialTlId ? [String(initialTlId)] : [])]));
                if (allStaffIds.length > 0) setAssignedStaffIds(allStaffIds);

                const mats = d.data.issued_materials || d.data.materials || [];
                if (mats.length > 0) {
                  setMaterialsIssued(
                    mats.map((m) => ({
                      id: m.id || uid(),
                      material: typeof m === "object" && m !== null
                        ? (m.material || m.material_name || m.name || m.code || m.material_code || m.material_id || "")
                        : String(m || ""),
                      issuedQty: m.issued_qty || m.quantity || 1,
                      used: m.used_qty ?? m.used ?? 0,
                      unit: m.unit || "pcs",
                      returnQty: m.return_qty ?? 0,
                      remarks: m.remarks || "",
                    }))
                  );
                }
              }
            }
          })
          .catch((err) => console.error("Error loading job for edit:", err));
    }, [editJobId]);

    const handleExcelImport = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
          if (lines.length <= 1) {
            alert("File appears empty or contains only headers.");
            return;
          }
          const newArticlesMap = {};
          const newMaterials = [];
          lines.slice(1).forEach((line) => {
            const cols = line.split(/,|;|\t/).map((c) => c.replace(/^["']|["']$/g, "").trim());
            if (!cols[0]) return;
            const artName = cols[0];
            const loc = cols[1] || "";
            const type = cols[2] || "";
            const estTime = cols[3] || "";
            const desc = cols[4] || "";
            const matName = cols[5] || "";
            const qty = cols[6] || "1";
            const unit = cols[7] || "pcs";

            if (!newArticlesMap[artName]) {
              newArticlesMap[artName] = {
                id: uid(),
                article: artName,
                location: loc,
                type: type,
                assignedStaff: [],
                estTime: estTime,
                initialPhoto: null,
                initialPhotoPreview: null,
                description: desc,
                teamLeaderRemarks: "",
                supervisorRemarks: "",
                materials: [],
                open: true,
              };
            }
            if (matName) {
              newArticlesMap[artName].materials.push({
                id: uid(),
                material: matName,
                qty: qty,
                unit: unit,
              });
              newMaterials.push({
                id: uid(),
                material: matName,
                issuedQty: qty,
                used: "0",
                unit: unit,
                returnQty: "0",
                remarks: "Imported from Excel",
              });
            }
          });
          const importedArticles = Object.values(newArticlesMap);
          if (importedArticles.length > 0) {
            setArticles((prev) => [...prev, ...importedArticles]);
            if (newMaterials.length > 0) {
              setMaterialsIssued((prev) => [...prev, ...newMaterials]);
            }
            alert(`Successfully imported ${importedArticles.length} articles and ${newMaterials.length} material items!`);
          } else {
            alert("No valid article rows found in file.");
          }
        } catch (err) {
          console.error("Excel import error:", err);
          alert("Failed to parse file. Please ensure it is a valid CSV file.");
        }
      };
      reader.readAsText(file);
      e.target.value = "";
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            setQSearch((prev) => ({
                ...prev,
                article: "",
            }));

            setFilteredQdb((prev) => ({
                ...prev,
                article: qdb.article.slice(0, 5),
            }));
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

    }, [qdb]);

  const isFieldStaff = (u) => {
      if (!u) return false;
      const r = String(u.role || "").toLowerCase().trim();
      if (["admin", "manager"].includes(r)) {
          return false;
      }
      return true;
  };

  const loadStaff = async () => {
      try {
          const res = await API.get("/users/staff-supervisors");
          if (res.data.success) {
              const allUsers = res.data.data || [];
              setStaffList(allUsers);
              setFilteredStaff(allUsers.filter(isFieldStaff));
              const map = {};
              allUsers.forEach((u) => {
                if (u.id && u.name) map[String(u.id)] = u.name;
              });
              setStaffNameMap((prev) => ({ ...map, ...prev }));
          }
      } catch (err) {
          console.log("Error:", err);
      }
  };


    const loadMasters = async () => {
      try {
          const res = await getMasters();
          const d = res.data?.data || {};
          const articles = (d.articles && d.articles.length > 0) ? d.articles : DEFAULT_QDB.article;
          const jobtitles = (d.job_titles && d.job_titles.length > 0) ? d.job_titles : (d.jobtitles || DEFAULT_QDB.jobtitle);
          const materials = (d.materials && d.materials.length > 0) ? d.materials : DEFAULT_QDB.material;

          if (res.data?.status || res.data?.success || d.materials) {
              setQdb({
                  article: articles,
                  jobtitle: jobtitles,
                  material: materials,
              });

              setFilteredQdb({
                  article: articles.slice(0, 5),
                  jobtitle: jobtitles.slice(0, 5),
                  material: materials.slice(0, 5),
              });
          }
      } catch (err) {
          console.log("loadMasters error, using defaults:", err);
      }
  };


  const handleQSearch = (key, value) => {

      setQSearch((prev) => ({
          ...prev,
          [key]: value,
      }));

      if (!value.trim()) {

          setFilteredQdb((prev) => ({
              ...prev,
              [key]: qdb[key].slice(0, 5),
          }));

          return;
      }

      const filtered = qdb[key].filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredQdb((prev) => ({
          ...prev,
          [key]: filtered,
      }));
  };

  /* ── Quick-add local databases (article / job title / material) ── */
  // const [qdb, setQdb] = useState(DEFAULT_QDB);
  // const [qdb, setQdb] = useState({
  //     article: [],
  //     jobtitle: [],
  //     material: [],
  // });
  // const [activeQPanel, setActiveQPanel] = useState(null); // 'article' | 'jobtitle' | 'material' | null
  // const [qInput, setQInput] = useState("");

  // const openQPanel = (key) => { setActiveQPanel(activeQPanel === key ? null : key); setQInput(""); };
  // const addQEntry = (key) => {
  //   const val = qInput.trim();
  //   if (!val) return;
  //   if (
  //       qdb[key].some(
  //           (e) => e.name.toLowerCase() === val.toLowerCase()
  //       )
  //   )
  //   return;
  //     setQdb((p) => ({
  //         ...p,
  //         [key]: [
  //             ...p[key],
  //             {
  //                 id: Date.now(),
  //                 name: val
  //             }
  //         ]
  //     }));
  //   setQInput("");
  // };
 
 const openQPanel = (key) => {
    setActiveQPanel((prev) => (prev === key ? null : key));
    setQInput("");
    setQSearch((prev) => ({ ...prev, [key]: "" }));
 };
 const addQEntry = async (key) => {

    const val = qInput.trim();

    if (!val) return;

    if (
        qdb[key].some(
            (e) => e.name.toLowerCase() === val.toLowerCase()
        )
    ) {
        alert("Already exists.");
        return;
    }

    let type = "";

    switch (key) {
        case "article":
            type = "article";
            break;

        case "jobtitle":
            type = "job_title";
            break;

        case "material":
            type = "material";
            break;

        default:
            return;
    }

    try {

        // setMasterLoading(true);
        setMasterLoading((prev) => ({
            ...prev,
            [key]: true,
        }));

        const res = await saveMaster({
            type,
            name: val,
        });

        if (res.data.status) {

            setQdb((prev) => ({
                ...prev,
                [key]: [
                    ...prev[key],
                    res.data.data
                ]
            }));

            setFilteredQdb((prev) => ({
                ...prev,
                [key]: qSearch[key]
                    ? [...prev[key], res.data.data]
                    : [...prev[key], res.data.data].slice(0, 5),
            }));

            setQInput("");

        }

    } catch (err) {

        alert(err?.response?.data?.message || "Unable to save.");

    } finally {

      setMasterLoading((prev) => ({
          ...prev,
          [key]: false,
      }));

    }

};
  // const deleteQEntry = (key, idx) =>
  //   setQdb((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));


  const deleteQEntry = async (key, id) => {

    let type = "";

    switch (key) {

        case "article":
            type = "article";
            break;

        case "jobtitle":
            type = "job_title";
            break;

        case "material":
            type = "material";
            break;

        default:
            return;
    }

    try {

        const res = await deleteMaster({
            type,
            id,
        });

        if (res.data.status) {

            setQdb((prev) => ({
                ...prev,
                [key]: prev[key].filter((item) => item.id !== id),
            }));

        }

    } catch (err) {

        alert(err?.response?.data?.message || "Unable to delete.");

    }

};

  /* ── Assign staff + team leader ── */
  const [assignedStaffIds, setAssignedStaffIds] = useState([]);
  const [teamLeaderId, setTeamLeaderId] = useState("");
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [staffNameMap, setStaffNameMap] = useState({});

  const getStaffName = (id) => {
    if (!id && id !== 0) return "";
    if (typeof id === "object" && id !== null) {
      return id.name || id.full_name || id.username || id.label || (id.id ? getStaffName(id.id) : "");
    }
    const cleanId = String(id).replace(/[[\\]]/g, "").trim();
    if (!cleanId) return "";

    const found = staffList.find((s) => String(s.id) === cleanId);
    if (found && found.name) return found.name;

    if (staffNameMap[cleanId]) return staffNameMap[cleanId];

    if (isNaN(cleanId) && !cleanId.startsWith("Staff #")) return cleanId;

    return `Staff #${cleanId}`;
  };

  const toggleArticleStaff = (articleId, staffId) => {
    setArticles((prev) =>
      prev.map((a) => {
        if (a.id !== articleId) return a;

        const current = Array.isArray(a.assignedStaff)
          ? a.assignedStaff
          : [];

        const id = String(staffId);

        const updated = current.includes(id)
          ? current.filter((x) => x !== id)
          : [...current, id];

        return {
          ...a,
          assignedStaff: updated,
        };
      })
    );
  };
  const getAllAssignedStaffIds = () => {
    return assignedStaffIds.map((id) => String(id));
  };
  const toggleArticleAllTeam = (articleId) => {
    setArticles((prev) =>
      prev.map((a) => {
        if (a.id !== articleId) return a;

        const allStaff = getAllAssignedStaffIds();

        const current = Array.isArray(a.assignedStaff)
          ? a.assignedStaff
          : [];

        const isAllSelected =
          allStaff.length > 0 &&
          allStaff.every((id) => current.includes(id));

        return {
          ...a,
          assignedStaff: isAllSelected ? [] : allStaff,
        };
      })
    );
  };   
  const addAssignedStaff = (id) => {
    const cleanId = String(id).replace(/[[\\]"']/g, "").trim();
    if (!cleanId || assignedStaffIds.includes(cleanId)) return;
    setAssignedStaffIds((p) => [...p, cleanId]);
    setFieldErrors((p) => ({ ...p, staff_id: "" }));
    setStaffPickerOpen(false);
  };
  const removeAssignedStaff = (id) => {
    const cleanId = String(id).replace(/[[\\]"']/g, "").trim();
    setAssignedStaffIds((p) => p.filter((s) => String(s).replace(/[[\\]"']/g, "").trim() !== cleanId));
    if (String(teamLeaderId).replace(/[[\\]"']/g, "").trim() === cleanId) setTeamLeaderId("");
  };

  /* ── Articles ── */
  /* ── Articles ── */
  const [articles, setArticles] = useState([emptyArticle()]);

  const toggleArticleOpen = (id) =>
    setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, open: !a.open } : a)));

  const updateArticleField = (id, field, value) =>
    setArticles((prev) => prev.map((a) => (a.id === id ? { ...a, [field]: value } : a)));

  const addArticleCard = () => setArticles((prev) => [...prev, emptyArticle()]);
  const removeArticleCard = (id) => setArticles((prev) => prev.filter((a) => a.id !== id));

  const handleArticlePhoto = (id, file) => {
    if (!file) return;
    const err = validateImageFile(file);
    setPhotoErrors((p) => ({ ...p, [`article_${id}`]: err || "" }));
    if (err) return;
    updateArticleField(id, "initialPhoto", file);
    updateArticleField(id, "initialPhotoPreview", URL.createObjectURL(file));
  };
  const clearArticlePhoto = (id) => {
    updateArticleField(id, "initialPhoto", null);
    updateArticleField(id, "initialPhotoPreview", null);
    setPhotoErrors((p) => ({ ...p, [`article_${id}`]: "" }));
  };

  const addArticleMaterial = (articleId) =>
    setArticles((prev) => prev.map((a) => a.id === articleId
      ? { ...a, materials: [...a.materials, { id: uid(), material: "", qty: "", unit: "" }] }
      : a));

  const updateArticleMaterial = (articleId, matId, field, value) =>
    setArticles((prev) => prev.map((a) => a.id === articleId
      ? { ...a, materials: a.materials.map((m) => (m.id === matId ? { ...m, [field]: value } : m)) }
      : a));

  const removeArticleMaterial = (articleId, matId) =>
    setArticles((prev) => prev.map((a) => a.id === articleId
      ? { ...a, materials: a.materials.filter((m) => m.id !== matId) }
      : a));

  /* ── Material Issued table ── */
  const [materialsIssued, setMaterialsIssued] = useState([emptyMaterialIssued()]);
  const addMaterialIssuedRow = () => setMaterialsIssued((p) => [...p, emptyMaterialIssued()]);
  const removeMaterialIssuedRow = (id) => setMaterialsIssued((p) => p.filter((m) => m.id !== id));
  const updateMaterialIssued = (id, field, value) =>
    setMaterialsIssued((p) => p.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  /* ── Photo Row table ── */
  const [photoRows, setPhotoRows] = useState([]);
  const totalPhotoCount = photoRows.reduce((sum, r) =>
    sum + (r.jobCardPhoto ? 1 : 0) + (r.beforePhoto ? 1 : 0) + r.afterPhotos.length, 0);

  const addPhotoRow = () => setPhotoRows((p) => [...p, emptyPhotoRow()]);
  const removePhotoRow = (id) => setPhotoRows((p) => p.filter((r) => r.id !== id));
  const updatePhotoRow = (id, field, value) =>
    setPhotoRows((p) => p.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const setPhotoRowError = (rowId, field, msg) =>
    setPhotoErrors((p) => ({ ...p, [`${rowId}_${field}`]: msg || "" }));

  const handleSinglePhotoUpload = (rowId, field, previewField, file) => {
    if (!file) return;
    const err = validateImageFile(file);
    setPhotoRowError(rowId, field, err);
    if (err) return;
    updatePhotoRow(rowId, field, file);
    updatePhotoRow(rowId, previewField, URL.createObjectURL(file));
  };
  const clearPhotoRowImage = (rowId, field, previewField) => {
    updatePhotoRow(rowId, field, null);
    updatePhotoRow(rowId, previewField, null);
    setPhotoRowError(rowId, field, "");
  };

  const handleAfterPhotosUpload = (rowId, files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const valid = [];
    let err = "";
    arr.forEach((f) => {
      const e = validateImageFile(f);
      if (e) err = e;
      else valid.push(f);
    });
    setPhotoRowError(rowId, "afterPhotos", err);
    if (!valid.length) return;
    setPhotoRows((prev) => prev.map((r) => r.id === rowId
      ? {
        ...r,
        afterPhotos: [...r.afterPhotos, ...valid],
        afterPreviews: [...r.afterPreviews, ...valid.map((f) => URL.createObjectURL(f))],
      }
      : r));
  };
  const removeAfterPhoto = (rowId, index) => {
    setPhotoRows((prev) => prev.map((r) => r.id === rowId
      ? {
        ...r,
        afterPhotos: r.afterPhotos.filter((_, i) => i !== index),
        afterPreviews: r.afterPreviews.filter((_, i) => i !== index),
      }
      : r));
  };


  const [previewImage, setPreviewImage] = useState(null);
  /* ── Validation (mirrors the Laravel Store request rules) ── */
  const hasImageErrors = () => Object.values(photoErrors).some((v) => !!v);

  const validateForm = () => {
    const errors = {};

    if (!clientName.trim()) errors.client_name = "Client name is required.";
    if (!location.trim()) errors.location = "Job location is required.";
    if (!jobTitle.trim()) errors.job_title = "Job title is required.";
    if (!jobStatus) errors.status = "Job status is required.";

    if (assignedStaffIds.length === 0) {
      errors.staff_id = "Please assign at least one staff member.";
    } else if (new Set(assignedStaffIds).size !== assignedStaffIds.length) {
      errors.staff_id = "Duplicate staff members are not allowed.";
    }

    if (!teamLeaderId) {
      errors.team_leader = "Please select a team leader.";
    } else if (!assignedStaffIds.includes(String(teamLeaderId))) {
      errors.team_leader = "Team leader must be selected from the assigned staff.";
    }

    if (articles.length === 0) {
      errors.articles_empty = "Please add at least one article/activity.";
    }

    const staffSet = new Set(assignedStaffIds);
    const articleErrors = {};
    articles.forEach((a) => {
      const aErr = {};
      if (!a.article.trim()) aErr.article = "Article name is required.";
      if (!a.location.trim()) aErr.location = "Article location is required.";
      if (!a.type.trim()) aErr.type = "Article type is required.";
      if (
        Array.isArray(a.assignedStaff) &&
        a.assignedStaff.some(
          (id) => !staffSet.has(String(id))
        )
      ) {
        aErr.assignedStaff =
          "Assigned staff must belong to the selected staff list.";
      }
      const matErrors = {};
      a.materials.forEach((m) => {
        const mErr = {};
        if (!m.material.trim()) mErr.material = "Material name is required.";
        if (m.qty === "" || m.qty === null || isNaN(m.qty) || Number(m.qty) < 0) mErr.qty = "Material quantity is required.";
        if (!String(m.unit).trim()) mErr.unit = "Material unit is required.";
        if (Object.keys(mErr).length) matErrors[m.id] = mErr;
      });
      if (Object.keys(matErrors).length) aErr.materials = matErrors;
      if (Object.keys(aErr).length) articleErrors[a.id] = aErr;
    });
    if (Object.keys(articleErrors).length) errors.articles = articleErrors;

    const miErrors = {};
    materialsIssued.forEach((m) => {
      const touched = m.material.trim() || m.issuedQty !== "" || String(m.unit).trim() || m.returnQty !== "" || m.used !== "";
      if (!touched) return;
      const mErr = {};
      if (!m.material.trim()) mErr.material = "Material name is required.";
      if (m.issuedQty === "" || isNaN(m.issuedQty) || Number(m.issuedQty) < 0) mErr.issuedQty = "Issued quantity is required.";
      if (!String(m.unit).trim()) mErr.unit = "Material unit is required.";
      if (m.returnQty !== "" && Number(m.returnQty) > Number(m.issuedQty || 0)) {
        mErr.returnQty = "Return quantity cannot be greater than issued quantity.";
      }
      if (Object.keys(mErr).length) miErrors[m.id] = mErr;
    });
    if (Object.keys(miErrors).length) errors.materialsIssued = miErrors;

    if (hasImageErrors()) {
      errors.images = "Please fix the highlighted image errors before submitting.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── Build FormData exactly matching the Store API payload ── */
  const buildFormData = (statusValue) => {
    const fd = new FormData();
    if (jobNumber) fd.append("job_number", jobNumber);
    fd.append("client_name", clientName);
    fd.append("location", location);
    fd.append("address", address || "");
    fd.append("job_title", jobTitle);
    fd.append("status", statusValue);

    assignedStaffIds.forEach((id) => fd.append("staff_id[]", id));
    if (teamLeaderId) {
      fd.append("team_leader_id", teamLeaderId);
      fd.append("team_leader", teamLeaderId);
    }

    articles.forEach((a, i) => {
      fd.append(`article[${i}][name]`, a.article);
      fd.append(`article[${i}][location]`, a.location);
      fd.append(`article[${i}][type]`, a.type);
      if (Array.isArray(a.assignedStaff) && a.assignedStaff.length > 0) {
        a.assignedStaff.forEach((staffId) => {
          fd.append(`article[${i}][assigned_staff][]`, staffId);
        });
      } else if (a.assignedStaff) {
        fd.append(`article[${i}][assigned_staff]`, a.assignedStaff);
      }
      fd.append(`article[${i}][est_time]`, a.estTime || "");
      fd.append(`article[${i}][description]`, a.description || "");
      fd.append(`article[${i}][team_leader_remarks]`, a.teamLeaderRemarks || "");
      fd.append(`article[${i}][supervisor_remarks]`, a.supervisorRemarks || "");
      if (a.initialPhoto) fd.append(`article[${i}][initial_photo]`, a.initialPhoto);
      a.materials.forEach((m, j) => {
        fd.append(`article[${i}][materials][${j}][name]`, m.material);
        fd.append(`article[${i}][materials][${j}][qty]`, m.qty);
        fd.append(`article[${i}][materials][${j}][unit]`, m.unit);
      });
    });

    materialsIssued.forEach((m, i) => {
      fd.append(`material_issued[${i}][name]`, m.material);
      fd.append(`material_issued[${i}][issued_qty]`, m.issuedQty);
      fd.append(`material_issued[${i}][used]`, m.used || "0");
      fd.append(`material_issued[${i}][unit]`, m.unit);
      fd.append(`material_issued[${i}][return_qty]`, m.returnQty || "0");
      fd.append(`material_issued[${i}][remarks]`, m.remarks || "");
    });

    photoRows.forEach((r, i) => {
      fd.append(`photo_row[${i}][description]`, r.description || "");
      fd.append(`photo_row[${i}][self_remark]`, r.selfRemark || "");
      fd.append(`photo_row[${i}][team_leader_remarks]`, r.teamLeaderRemarks || "");
      fd.append(`photo_row[${i}][supervisor_remarks]`, r.supervisorRemarks || "");
      if (r.jobCardPhoto) fd.append(`photo_row[${i}][job_card_photo]`, r.jobCardPhoto);
      if (r.beforePhoto) fd.append(`photo_row[${i}][before_photo]`, r.beforePhoto);
      r.afterPhotos.forEach((f) => fd.append(`photo_row[${i}][after_photos][]`, f));
    });

    return fd;
  };

  /** Maps Laravel-style dotted validation error keys back onto the current row ids so
   *  messages can be shown under the matching input. */
  const parseServerErrors = (errors) => {
    const fe = {};
    Object.keys(errors).forEach((key) => {
      const msg = errors[key][0];
      let m;
      if (key === "client_name") fe.client_name = msg;
      else if (key === "location") fe.location = msg;
      else if (key === "job_title") fe.job_title = msg;
      else if (key === "status") fe.status = msg;
      else if (key.startsWith("staff_id")) fe.staff_id = msg;
      else if (key.startsWith("team_leader_id")) fe.team_leader = msg;
      else if ((m = key.match(/^article\.(\d+)\.materials\.(\d+)\.(\w+)$/))) {
        const [, ai, mi, field] = m;
        const article = articles[ai];
        const matId = article?.materials?.[mi]?.id;
        if (article && matId) {
          fe.articles = fe.articles || {};
          fe.articles[article.id] = fe.articles[article.id] || {};
          fe.articles[article.id].materials = fe.articles[article.id].materials || {};
          fe.articles[article.id].materials[matId] = fe.articles[article.id].materials[matId] || {};
          fe.articles[article.id].materials[matId][field === "name" ? "material" : field] = msg;
        }
      } else if ((m = key.match(/^article\.(\d+)\.(\w+)$/))) {
        const [, ai, field] = m;
        const article = articles[ai];
        if (article) {
          fe.articles = fe.articles || {};
          fe.articles[article.id] = fe.articles[article.id] || {};
          fe.articles[article.id][field === "name" ? "article" : field] = msg;
        }
      } else if ((m = key.match(/^material_issued\.(\d+)\.(\w+)$/))) {
        const [, mi, field] = m;
        const mat = materialsIssued[mi];
        if (mat) {
          fe.materialsIssued = fe.materialsIssued || {};
          fe.materialsIssued[mat.id] = fe.materialsIssued[mat.id] || {};
          const mapped = field === "issued_qty" ? "issuedQty" : field === "return_qty" ? "returnQty" : field === "name" ? "material" : field;
          fe.materialsIssued[mat.id][mapped] = msg;
        }
      } else if ((m = key.match(/^photo_row\.(\d+)\.(\w+)/))) {
        const [, pi, field] = m;
        const row = photoRows[pi];
        if (row) {
          fe.photoRows = fe.photoRows || {};
          fe.photoRows[row.id] = fe.photoRows[row.id] || {};
          fe.photoRows[row.id][field] = msg;
        }
      }
    });
    return fe;
  };

  /* ── Submit / Save Draft ── */
  const submitJob = async (statusValue, { skipValidation = false } = {}) => {
    setServerErrors([]);
    if (!skipValidation) {
      if (!validateForm()) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    } else {
      setFieldErrors({});
    }

    const fd = buildFormData(statusValue);
    if (editJobId) {
      fd.append("job_id", editJobId);
    }
    const isDraft = statusValue === "Draft";
    isDraft ? setSavingDraft(true) : setSubmitting(true);

    try {
      const res = editJobId
        ? await API.post(`/job/update/${editJobId}?team_leader_id=${teamLeaderId || ""}&team_leader=${teamLeaderId || ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
        : await createJob(fd);

      if (res.data.status || res.data.success) {
        setSubmitSuccess(true);
        setSuccessMessage(isDraft ? "Job saved as draft!" : editJobId ? "Job updated successfully!" : "Job created successfully!");
        if (onSaveSuccess) {
          setTimeout(() => onSaveSuccess(), 1000);
        } else {
          setTimeout(() => navigate("/dashboard"), 1500);
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) {
        const msgs = Object.values(data.errors).flat();
        setServerErrors(msgs);
        setFieldErrors((prev) => ({ ...prev, ...parseServerErrors(data.errors) }));
      } else {
        setServerErrors([data?.message || "Something went wrong. Please try again."]);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      isDraft ? setSavingDraft(false) : setSubmitting(false);
    }
  };

  const handleSubmit = () => submitJob(jobStatus);
  const handleSaveDraft = () => submitJob("Draft", { skipValidation: true });

  /* ── Close quick/staff panels on outside click ── */
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest(".cjn-qwrap")) {
        setActiveQPanel(null);
        setStaffPickerOpen(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  const handleStaffSearch = (e) => {
      const value = e.target.value;

      setStaffSearch(value);

      const fieldStaffOnly = staffList.filter(isFieldStaff);

      if (!value.trim()) {
          setFilteredStaff(fieldStaffOnly);
          return;
      }

      const filtered = fieldStaffOnly.filter((staff) =>
          staff.name.toLowerCase().includes(value.toLowerCase())
      );

      setFilteredStaff(filtered);
  };
  // const availableStaffForPicker = staffList.filter((s) => !assignedStaffIds.includes(String(s.id)));
    const availableStaffForPicker = filteredStaff.filter(
      (s) => !assignedStaffIds.includes(String(s.id))
  );
  const busy = submitting || savingDraft;

  

  /* ═══════════════════════════ Render ═══════════════════════════ */
  return (
    <>
      <style>{STYLES}</style>

      {busy && (
        <div className="cjn-overlay">
          <div className="cjn-overlay-box">
            <div className="cjn-overlay-spin" />
            <div className="cjn-overlay-title">{savingDraft ? "Saving Draft…" : "Submitting Job…"}</div>
            <div className="cjn-overlay-sub">Please wait, uploading data &amp; photos</div>
          </div>
        </div>
      )}

      <div className="cjn-wrap">

        {submitSuccess && (
          <div className="cjn-banner"><CheckIcon /> {successMessage} Redirecting to reports…</div>
        )}

        {serverErrors.length > 0 && (
          <div className="cjn-error">
            <div className="t"><AlertIcon /> Please fix the following errors:</div>
              <ul>
                  {serverErrors.map((e, i) => (
                      <li key={i}>{e}</li>
                  ))}
              </ul>
          </div>
        )}

        {!serverErrors.length && Object.keys(fieldErrors).length > 0 && (
          <div className="cjn-error">
            <div className="t"><AlertIcon /> Please fix the highlighted fields before submitting.</div>
          </div>
        )}

        <div className="cjn-alert">
          <AlertIcon />
          <span>
            All fields marked with <strong>*</strong> are required. Return quantity cannot exceed issued quantity.
            Images must be JPG, JPEG, or PNG and under <strong>5&nbsp;MB</strong>.
          </span>
        </div>

        {/* ══ SECTION 1: Basic Job Details ══ */}
        <div className="cjn-card">
          <div className="cjn-card-hd">
            <h2>Basic Job Details</h2>
            <span className="cjn-jobno-pill">
              Job #
              <input value={jobNumber} onChange={(e) => setJobNumber(e.target.value)} title="Auto-generated · click to edit" />
            </span>
          </div>

          <div className="cjn-card-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="cjn-grid3">
              <div className="cjn-fg">
                <label className="cjn-label">Client Name<span className="req">*</span></label>
                <input
                  className={`cjn-input${fieldErrors.client_name ? " err" : ""}`}
                  placeholder="Enter client name" value={clientName}
                  onChange={(e) => { setClientName(e.target.value); setFieldErrors((p) => ({ ...p, client_name: "" })); }}
                />
                {fieldErrors.client_name && <span className="cjn-err-msg">{fieldErrors.client_name}</span>}
              </div>
              <div className="cjn-fg">
                <label className="cjn-label">Location<span className="req">*</span></label>
                <input
                  className={`cjn-input${fieldErrors.location ? " err" : ""}`}
                  placeholder="City / area" value={location}
                  onChange={(e) => { setLocation(e.target.value); setFieldErrors((p) => ({ ...p, location: "" })); }}
                />
                {fieldErrors.location && <span className="cjn-err-msg">{fieldErrors.location}</span>}
              </div>
              <div className="cjn-fg">
                <label className="cjn-label">Address</label>
                <textarea className="cjn-textarea" rows={1} placeholder="Full address (optional)"
                  value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
            </div>

            <div className="cjn-grid3">
              {/* Assign staff + team leader */}
              <div className="cjn-fg">
                <label className="cjn-label">Assign Staff<span className="req">*</span></label>
                <div className={`cjn-staffbox${fieldErrors.staff_id ? " err" : ""}`}>
                  <div className="cjn-tags">
                    {assignedStaffIds.length === 0 && (
                      <span style={{ fontSize: ".78rem", color: "#c0c4cc" }}>No staff assigned yet…</span>
                    )}
                    {assignedStaffIds.map((id) => {
                      const isLeader = teamLeaderId === id;
                      return (
                        <span className={`cjn-tag${isLeader ? " leader" : ""}`} key={id}>
                          {isLeader && <CrownIcon />}{getStaffName(id)}
                          <span className="x" onClick={() => removeAssignedStaff(id)}>×</span>
                        </span>
                      );
                    })}
                  </div>
                  <div className="cjn-qwrap">
                    <button type="button" className="cjn-linkbtn" onClick={() => {
                        setStaffPickerOpen((v) => {
                            const newState = !v;

                            if (newState) {
                                // Opening popup
                                setStaffSearch("");
                                setFilteredStaff(staffList.filter(isFieldStaff));
                            }

                            return newState;
                        });
                    }}>
                      + Add staff member…
                    </button>
                    {staffPickerOpen && (
                      <div className="cjn-panel">
                        <div className="cjn-panel-title">Add from field staff</div>


                          <input
                              type="text"
                              className="cjn-input"
                              placeholder="Search staff..."
                              value={staffSearch}
                              onChange={handleStaffSearch}
                              style={{ marginBottom: "10px" }}
                          />
                        <div className="cjn-panel-list">
                          {availableStaffForPicker.length === 0 && (
                            <div className="cjn-panel-empty">All field staff already assigned</div>
                          )}
                          {filteredStaff.length > 0 && (
                            <div
                              className="cjn-panel-item"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                const allIds = filteredStaff.map((s) => String(s.id));

                                setAssignedStaffIds((prev) => {
                                  const merged = [...new Set([...prev, ...allIds])];
                                  return merged;
                                });

                                setFieldErrors((p) => ({
                                  ...p,
                                  staff_id: "",
                                }));
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>
                                ☑ All Team
                              </span>

                              <span style={{ color: "#6366f1", fontWeight: 600 }}>
                                Select All
                              </span>
                            </div>
                          )}

                          {availableStaffForPicker.map((s) => {
                            const selected = assignedStaffIds.includes(String(s.id));

                            return (
                              <div
                                className="cjn-panel-item"
                                style={{ cursor: "pointer" }}
                                key={s.id}
                                onClick={() => {
                                  if (selected) {
                                    removeAssignedStaff(s.id);
                                  } else {
                                    addAssignedStaff(s.id);
                                  }
                                }}
                              >
                                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    readOnly
                                  />

                                  {s.name}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {fieldErrors.staff_id && <span className="cjn-err-msg">{fieldErrors.staff_id}</span>}

                <div className="cjn-fg" style={{ marginTop: ".6rem" }}>
                  <label className="cjn-label">Team Leader<span className="req">*</span></label>
                  <SearchableSelect
                    options={assignedStaffIds.map((id) => {
                      const cleanId = String(id).replace(/[[\\]"']/g, "").trim();
                      const staffObj = (staffList || []).find((s) => s && String(s.id).replace(/[[\\]"']/g, "").trim() === cleanId);
                      const name = getStaffName(cleanId);
                      const r = String(staffObj?.role || "").toLowerCase().trim();
                      const isLeader = r.includes("leader") || r === "team_leader" || r === "team leader" || r === "teamleader";
                      return {
                        value: cleanId,
                        label: isLeader ? `👑 ${name} (Team Leader)` : name,
                      };
                    })}
                    value={teamLeaderId}
                    onChange={(val) => {
                      setTeamLeaderId(val);
                      setFieldErrors((p) => ({ ...p, team_leader: "" }));
                    }}
                    disabled={assignedStaffIds.length === 0}
                    placeholder={assignedStaffIds.length === 0 ? "— Assign staff first —" : "— Select team leader —"}
                    searchPlaceholder="Search team leader..."
                    error={!!fieldErrors.team_leader}
                  />
                  {fieldErrors.team_leader && <span className="cjn-err-msg">{fieldErrors.team_leader}</span>}
                  <div className="cjn-help">One team leader per job · chosen from assigned staff (👑)</div>
                </div>
              </div>

              {/* Job title (typeable + quick options) */}
              <div className="cjn-fg">
                <label className="cjn-label">Job Title<span className="req">*</span></label>
                <SearchableSelect
                  options={qdb.jobtitle.map((t) => ({ value: t.name, label: t.name }))}
                  value={jobTitle}
                  onChange={(val) => { setJobTitle(val); setFieldErrors((p) => ({ ...p, job_title: "" })); }}
                  placeholder="Type job title or select…"
                  searchPlaceholder="Search or type job title..."
                  allowCustom={true}
                  error={!!fieldErrors.job_title}
                />
                {fieldErrors.job_title && <span className="cjn-err-msg">{fieldErrors.job_title}</span>}
              </div>

              <div className="cjn-fg">
                <label className="cjn-label">Job Status<span className="req">*</span></label>
                <SearchableSelect
                  options={JOB_STATUS_OPTIONS}
                  value={jobStatus}
                  onChange={(val) => { setJobStatus(val); setFieldErrors((p) => ({ ...p, status: "" })); }}
                  placeholder="Select Status"
                  searchPlaceholder="Search status..."
                  error={!!fieldErrors.status}
                />
                {fieldErrors.status && <span className="cjn-err-msg">{fieldErrors.status}</span>}
                <div className="cjn-help">Default status is <strong>Ongoing</strong>.</div>
              </div>
            </div>
          </div>

          {/* Quick add strip */}
          <div className="cjn-quickstrip">
            <div className="lbl">Quick database entries:</div>
            <div className="cjn-qbtn-row">
              {["article", "jobtitle", "material"].map((key) => (
                <div className="cjn-qwrap" key={key}>
                  <div className="cjn-qbtn" onClick={() => openQPanel(key)} style={{ cursor: "pointer" }}>
                    <span className="l">{key === "jobtitle" ? "Job Title" : key === "article" ? "Article" : "Material"}</span>
                    <span className="a">⚙ Manage</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ SECTION 2: Articles & Job Lines ══ */}
        <div className="cjn-card">
          <div className="cjn-card-hd">
            <div>
              <h2>Articles &amp; Job Lines</h2>
              <div className="sub">Each card = one article · add materials inside each article</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", fontSize: ".82rem", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", fontWeight: 600, color: "#4f46e5" }}>
                📥 Import Excel / CSV
                <input type="file" accept=".csv,.xlsx,.xls,.txt" style={{ display: "none" }} onChange={handleExcelImport} />
              </label>
              <button type="button" className="cjn-addcardbtn" onClick={addArticleCard}>
                <PlusIcon /> Add Article
              </button>
            </div>
          </div>

          <div className="cjn-card-body">
            {articles.map((a, idx) => {
              const aErr = fieldErrors.articles?.[a.id] || {};
              const hasArticleError = Object.keys(aErr).length > 0;
              return (
                <div className="cjn-artcard" key={a.id}>
                  <div className="cjn-artcard-hd" onClick={() => toggleArticleOpen(a.id)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <div className="cjn-artcard-num">{idx + 1}</div>
                      <div style={{ minWidth: 0 }}>
                        <span className="cjn-artcard-title" style={{ color: a.article ? "#1f2937" : "#9ca3af" }}>
                          {a.article || "— Select article —"}
                        </span>
                        {a.location && <span className="cjn-artcard-loc">{a.location}{a.type ? ` · ${a.type}` : ""}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      {hasArticleError && <span className="cjn-artcard-badge errbadge">fix errors</span>}
                      <span className={`cjn-artcard-badge${a.materials.length === 0 ? " empty" : ""}`}>
                        {a.materials.length} material{a.materials.length !== 1 ? "s" : ""}
                      </span>
                      <button type="button" className="cjn-iconbtn" title="Remove article"
                        onClick={(e) => { e.stopPropagation(); removeArticleCard(a.id); }}>
                        <TrashIcon />
                      </button>
                      <ChevronIcon open={a.open} />
                    </div>
                  </div>

                  {a.open && (
                    <div className="cjn-artcard-body">
                      <div className="cjn-artfields">
                        <div className="cjn-fg">
                          <label className="cjn-label">Article<span className="req">*</span></label>
                          <SearchableSelect
                            options={qdb.article.map((item) => typeof item === "object" ? { value: item.name, label: item.name } : { value: item, label: item })}
                            value={a.article}
                            onChange={(val) => updateArticleField(a.id, "article", val)}
                            placeholder="— Select Article —"
                            searchPlaceholder="Search article..."
                            error={!!aErr.article}
                          />
                          {aErr.article && <span className="cjn-err-msg">{aErr.article}</span>}
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-label">Location<span className="req">*</span></label>
                          <input className={`cjn-input${aErr.location ? " err" : ""}`} placeholder="e.g. Inside Kitchen" value={a.location}
                            onChange={(e) => updateArticleField(a.id, "location", e.target.value)} />
                          {aErr.location && <span className="cjn-err-msg">{aErr.location}</span>}
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-label">Type<span className="req">*</span></label>
                          <SearchableSelect
                            options={ARTICLE_TYPE_OPTIONS}
                            value={a.type}
                            onChange={(val) => updateArticleField(a.id, "type", val)}
                            placeholder="— Select Type —"
                            searchPlaceholder="Search type..."
                            error={!!aErr.type}
                          />
                          {aErr.type && <span className="cjn-err-msg">{aErr.type}</span>}
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-label">Assigned Staff</label>
                          <MultiSelectStaffDropdown
                            assignedStaff={a.assignedStaff}
                            assignedStaffIds={assignedStaffIds}
                            getStaffName={getStaffName}
                            onToggleAllTeam={() => toggleArticleAllTeam(a.id)}
                            onToggleStaff={(staffId) => toggleArticleStaff(a.id, staffId)}
                            error={!!aErr.assignedStaff}
                          />
                          {aErr.assignedStaff && <span className="cjn-err-msg">{aErr.assignedStaff}</span>}
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-label">Est. Time</label>
                          <input className="cjn-input" placeholder="e.g. 4 Hrs." value={a.estTime}
                            onChange={(e) => updateArticleField(a.id, "estTime", e.target.value)} />
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-label">Initial Photo</label>
                          <label
                              className={`cjn-photobox${a.initialPhotoPreview ? " has-image" : ""}${photoErrors[`article_${a.id}`] ? " err" : ""}`}
                          >

                              {a.initialPhotoPreview ? (
                                  <>
                                      <img
                                          src={a.initialPhotoPreview}
                                          alt="Initial preview"
                                          className="cjn-photo-preview"
                                      />
                                      <div className="cjn-photo-overlay">
                                          <button
                                              type="button"
                                              className="cjn-photo-btn view"
                                              title="View Photo"
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setPreviewImage(a.initialPhotoPreview);
                                              }}
                                          >
                                              👁
                                          </button>
                                          <button
                                              type="button"
                                              className="cjn-photo-btn remove"
                                              title="Remove Photo"
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  clearArticlePhoto(a.id);
                                              }}
                                          >
                                              <XIcon />
                                          </button>
                                      </div>
                                  </>
                              ) : (
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b" }}>
                                      <CameraIcon />
                                      <span style={{ fontSize: "0.78rem", fontWeight: 500 }}>Upload Photo</span>
                                  </div>
                              )}

                              <input
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png"
                                  style={{ display: "none" }}
                                  onChange={(e) => handleArticlePhoto(a.id, e.target.files?.[0])}
                              />

                          </label>
                        </div>
                      </div>

                      <div className="cjn-fg">
                        <label className="cjn-matcol-lbl">Job Description</label>
                        <textarea className="cjn-tinp" rows={2} placeholder="Describe the job..."
                          value={a.description} onChange={(e) => updateArticleField(a.id, "description", e.target.value)} />
                      </div>

                      <div className="cjn-grid2" style={{ marginTop: 12 }}>
                        <div className="cjn-fg">
                          <label className="cjn-matcol-lbl">Team Leader Remarks</label>
                          <input
                            className="cjn-tinp"
                            placeholder="Team leader remark..."
                            value={a.teamLeaderRemarks}
                            onChange={(e) =>
                              updateArticleField(
                                a.id,
                                "teamLeaderRemarks",
                                e.target.value
                              )
                            }
                            readOnly={isSupervisor}
                          />
                        </div>
                        <div className="cjn-fg">
                          <label className="cjn-matcol-lbl">Supervisor Remarks</label>
                          <input className="cjn-tinp" placeholder="Supervisor remark..." value={a.supervisorRemarks}
                            onChange={(e) => updateArticleField(a.id, "supervisorRemarks", e.target.value)} />
                        </div>
                      </div>

                      {/* Materials for this article */}
                      <div className="cjn-matsub">
                        <div className="cjn-matsub-hd">
                          <span>Materials for this article</span>
                          <button type="button" className="addlink" onClick={() => addArticleMaterial(a.id)}>
                            <PlusIcon /> Add Material
                          </button>
                        </div>
                        {a.materials.length > 0 && (
                          <div className="cjn-matrow" style={{ marginBottom: 4 }}>
                            <span className="cjn-matcol-lbl">Material</span>
                            <span className="cjn-matcol-lbl">Qty</span>
                            <span className="cjn-matcol-lbl">Unit</span>
                            <span />
                          </div>
                        )}
                        {a.materials.length === 0 && (
                          <div className="cjn-matempty">No materials added yet. Click "+ Add Material" to add.</div>
                        )}
                        {a.materials.map((m) => {
                          const mErr = aErr.materials?.[m.id] || {};
                          return (
                            <div className="cjn-matrow" key={m.id}>
                              <div>
                                <SearchableSelect
                                  options={qdb.material.map((o) => typeof o === "object" && o !== null ? (o.name || o.material_name || o.label || o.value || String(o)) : String(o))}
                                  value={m.material}
                                  onChange={(val) => updateArticleMaterial(a.id, m.id, "material", val)}
                                  placeholder="— Select —"
                                  searchPlaceholder="Search material..."
                                  allowCustom={true}
                                  error={!!mErr.material}
                                />
                                {mErr.material && <span className="cjn-err-msg">{mErr.material}</span>}
                              </div>
                              <div>
                                <input className={`cjn-tinp${mErr.qty ? " err" : ""}`} type="number" placeholder="Qty" value={m.qty}
                                  onChange={(e) => updateArticleMaterial(a.id, m.id, "qty", e.target.value)} />
                                {mErr.qty && <span className="cjn-err-msg">{mErr.qty}</span>}
                              </div>
                              <div>
                                <input className={`cjn-tinp${mErr.unit ? " err" : ""}`} placeholder="Unit" value={m.unit}
                                  onChange={(e) => updateArticleMaterial(a.id, m.id, "unit", e.target.value)} />
                                {mErr.unit && <span className="cjn-err-msg">{mErr.unit}</span>}
                              </div>
                              <button type="button" className="cjn-iconbtn" onClick={() => removeArticleMaterial(a.id, m.id)}>
                                <TrashIcon />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                
              );
            })}

            <button type="button" className="cjn-dashedbtn" onClick={addArticleCard}>
              + Add More Article
            </button>
          </div>
        </div>

        {/* ══ SECTION 3: Material Issued ══ */}
        <div className="cjn-card">
          <div className="cjn-card-hd">
            <div>
              <h2>Material Issued</h2>
              <div className="sub">Material Name · Issued Qty · Used Qty · Return Qty · Unit · Remarks</div>
            </div>
          </div>
          <div className="cjn-card-body">
            <div className="cjn-table-wrap">
              <table className="cjn-table warn">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>S.No</th>
                    <th>Material Name</th>
                    <th style={{ width: 100 }}>Issued Qty</th>
                    <th style={{ width: 100 }}>Used Qty</th>
                    <th style={{ width: 100 }}>Return Qty</th>
                    <th style={{ width: 70 }}>Unit</th>
                    <th>Remarks</th>
                    <th style={{ width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {materialsIssued.map((m, i) => {
                    const mErr = fieldErrors.materialsIssued?.[m.id] || {};
                    return (
                      <tr key={m.id}>
                        <td><div className="cjn-sno">{i + 1}</div></td>
                        <td>
                          <SearchableSelect
                            options={qdb.material.map((o) => typeof o === "object" ? o.name : o)}
                            value={m.material}
                            onChange={(val) => updateMaterialIssued(m.id, "material", val)}
                            placeholder="— Select Material —"
                            searchPlaceholder="Search material..."
                            style={{ minWidth: 160 }}
                            error={!!mErr.material}
                          />
                          {mErr.material && <span className="cjn-err-msg">{mErr.material}</span>}
                        </td>
                        <td>
                          <input className={`cjn-tinp${mErr.issuedQty ? " err" : ""}`} type="number" style={{ width: 80 }} placeholder="0" value={m.issuedQty}
                            onChange={(e) => updateMaterialIssued(m.id, "issuedQty", e.target.value)} />
                          {mErr.issuedQty && <span className="cjn-err-msg">{mErr.issuedQty}</span>}
                        </td>
                        <td>
                          <input className="cjn-tinp" type="number" style={{ width: 80 }} placeholder="0" value={m.used}
                            onChange={(e) => updateMaterialIssued(m.id, "used", e.target.value)} />
                        </td>
                        <td>
                          <input className={`cjn-tinp${mErr.returnQty ? " err" : ""}`} type="number" style={{ width: 80 }} placeholder="0" value={m.returnQty}
                            onChange={(e) => updateMaterialIssued(m.id, "returnQty", e.target.value)} />
                          {mErr.returnQty && <span className="cjn-err-msg">{mErr.returnQty}</span>}
                        </td>
                        <td>
                          <input className={`cjn-tinp${mErr.unit ? " err" : ""}`} style={{ width: 55 }} value={m.unit}
                            onChange={(e) => updateMaterialIssued(m.id, "unit",  e.target.value
                .toUpperCase()
                .replace(/[^A-Z]/g, ""))} />
                          {mErr.unit && <span className="cjn-err-msg">{mErr.unit}</span>}
                        </td>
                        <td><input className="cjn-tinp" placeholder="Remarks..." value={m.remarks}
                          onChange={(e) => updateMaterialIssued(m.id, "remarks", e.target.value)} /></td>
                        <td>
                          <button type="button" className="cjn-iconbtn" onClick={() => removeMaterialIssuedRow(m.id)}>
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="cjn-dashedbtn" style={{ marginTop: 12 }} onClick={addMaterialIssuedRow}>
              + Add More Material
            </button>
          </div>
        </div>

        {/* ══ SECTION 4: Photo Row ══ */}
        <div className="cjn-card">
          <div className="cjn-card-hd">
            <div>
              <h2>Photo Row</h2>
              <div className="sub">Description · Job Card Photo · Before Photo · After Photo(s) · Remarks</div>
            </div>
            <span style={{ fontSize: ".72rem", color: "#9ca3af", background: "#f3f4f6", borderRadius: 6, padding: "4px 8px", fontWeight: 600 }}>
              {totalPhotoCount} photo{totalPhotoCount !== 1 ? "s" : ""} added
            </span>
          </div>
          <div className="cjn-table-wrap">
            <table className="cjn-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }}>S.No</th>
                  <th style={{ minWidth: 80 }}>Description</th>
                  <th style={{ width: 100 }}>JobCard</th>
                  <th style={{ width: 100 }}>Before </th>
                  <th style={{ minWidth: 100 }}>After</th>
                  <th style={{ minWidth: 80 }}>Self Remark</th>
                  <th style={{ minWidth: 100 }}>TeamLeader </th>
                  <th style={{ minWidth: 100 }}>Supervisor Remark</th>
                  <th style={{ width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {photoRows.map((r, i) => {
                  const rErr = fieldErrors.photoRows?.[r.id] || {};
                  return (
                    <tr key={r.id}>
                      <td><div className="cjn-sno">{i + 1}</div></td>
                      <td><input className="cjn-tinp" placeholder="Describe article or full job..." value={r.description}
                        onChange={(e) => updatePhotoRow(r.id, "description", e.target.value)} /></td>
                      <td>
                        <div className="cjn-photocell">
                          <label className={`cjn-photobox${photoErrors[`${r.id}_jobCardPhoto`] || rErr.job_card_photo ? " err" : ""}`} title="Upload job card photo">
                            {r.jobCardPreview ? <img src={r.jobCardPreview} alt="" /> : <CameraIcon />}
                            <input type="file" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }}
                              onChange={(e) => handleSinglePhotoUpload(r.id, "jobCardPhoto", "jobCardPreview", e.target.files?.[0])} />
                            {r.jobCardPreview && (
                              <span className="cjn-photo-remove" onClick={(e) => { e.preventDefault(); clearPhotoRowImage(r.id, "jobCardPhoto", "jobCardPreview"); }}>
                                <XIcon />
                              </span>
                            )}
                          </label>
                          {(photoErrors[`${r.id}_jobCardPhoto`] || rErr.job_card_photo) && (
                            <span className="cjn-photo-err-msg">{photoErrors[`${r.id}_jobCardPhoto`] || rErr.job_card_photo}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="cjn-photocell">
                          <label className={`cjn-photobox${photoErrors[`${r.id}_beforePhoto`] || rErr.before_photo ? " err" : ""}`} title="Upload before photo">
                            {r.beforePreview ? <img src={r.beforePreview} alt="" /> : <CameraIcon />}
                            <input type="file" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }}
                              onChange={(e) => handleSinglePhotoUpload(r.id, "beforePhoto", "beforePreview", e.target.files?.[0])} />
                            {r.beforePreview && (
                              <span className="cjn-photo-remove" onClick={(e) => { e.preventDefault(); clearPhotoRowImage(r.id, "beforePhoto", "beforePreview"); }}>
                                <XIcon />
                              </span>
                            )}
                          </label>
                          {(photoErrors[`${r.id}_beforePhoto`] || rErr.before_photo) && (
                            <span className="cjn-photo-err-msg">{photoErrors[`${r.id}_beforePhoto`] || rErr.before_photo}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                          {r.afterPreviews.map((url, k) => (
                            <div className="cjn-photobox" key={k}>
                              <img src={url} alt="" />
                              <span className="cjn-photo-remove" onClick={() => removeAfterPhoto(r.id, k)}>
                                <XIcon />
                              </span>
                            </div>
                          ))}
                          <label className={`cjn-photobox${photoErrors[`${r.id}_afterPhotos`] ? " err" : ""}`} title="Add after photos">
                            <PlusIcon />
                            <input type="file" accept="image/jpeg,image/jpg,image/png" multiple style={{ display: "none" }}
                              onChange={(e) => handleAfterPhotosUpload(r.id, e.target.files)} />
                          </label>
                        </div>
                        {photoErrors[`${r.id}_afterPhotos`] && (
                          <div className="cjn-photo-err-msg" style={{ margin: "4px auto 0", textAlign: "center" }}>{photoErrors[`${r.id}_afterPhotos`]}</div>
                        )}
                      </td>
                      <td><input className="cjn-tinp" placeholder="Self remark..." value={r.selfRemark}
                        onChange={(e) => updatePhotoRow(r.id, "selfRemark", e.target.value)} /></td>
                      <td><input className="cjn-tinp" placeholder={isSupervisor ? "Read-only (Team Leader only)" : "Team leader remark..."} value={r.teamLeaderRemarks}
                        onChange={(e) => updatePhotoRow(r.id, "teamLeaderRemarks", e.target.value)} readOnly={isSupervisor} /></td>
                      <td><input className="cjn-tinp" placeholder="Supervisor remark..." value={r.supervisorRemarks}
                        onChange={(e) => updatePhotoRow(r.id, "supervisorRemarks", e.target.value)} /></td>
                      <td>
                        <button type="button" className="cjn-iconbtn" onClick={() => removePhotoRow(r.id)}>
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: 14 }}>
            <button type="button" className="cjn-dashedbtn" onClick={addPhotoRow}>
              + Add More Photo Row
            </button>
          </div>
        </div>

        {/* ══ Footer ══ */}
        <div className="cjn-footer">
          <button type="button" className="cjn-btn-ghost" disabled={busy}>Cancel</button>
          <button type="button" className="cjn-btn-ghost" disabled={busy} onClick={handleSaveDraft}>
            {savingDraft ? <><SpinnerIcon /> Saving…</> : "Save as Draft"}
          </button>
          <button type="button" className="cjn-btn-primary" onClick={handleSubmit} disabled={busy || submitSuccess}>
            {submitting ? <><SpinnerIcon /> Submitting…</> : <><CheckIcon /> Create Job</>}
          </button>
        </div>

      </div>

      {/* ===========================
          IMAGE PREVIEW MODAL
      =========================== */}

      {previewImage && (
          <div
              className="cjn-preview-overlay"
              onClick={() => setPreviewImage(null)}
          >
              <div
                  className="cjn-preview-container"
                  onClick={(e) => e.stopPropagation()}
              >

                  <button
                      type="button"
                      className="cjn-preview-close"
                      onClick={() => setPreviewImage(null)}
                  >
                      ✕
                  </button>

                  <img
                      src={previewImage}
                      alt="Preview"
                      className="cjn-preview-image"
                  />

              </div>
          </div>
      )}

      {/* ── MASTER DATABASE MANAGEMENT MODAL ── */}
      {activeQPanel && (
        <div className="cjn-modal-backdrop" onClick={() => setActiveQPanel(null)}>
          <div className="cjn-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="cjn-modal-hd">
              <div>
                <h3>⚙ Manage {activeQPanel === "jobtitle" ? "Job Titles" : activeQPanel === "article" ? "Articles" : "Materials"}</h3>
                <p>Add, search, or remove master database entries</p>
              </div>
              <button type="button" className="cjn-modal-close" onClick={() => setActiveQPanel(null)}>✕</button>
            </div>

            <div className="cjn-modal-body">
              <input
                type="text"
                className="cjn-input"
                placeholder={`Search ${activeQPanel === "article" ? "articles" : activeQPanel === "jobtitle" ? "job titles" : "materials"}...`}
                value={qSearch[activeQPanel] || ""}
                onChange={(e) => handleQSearch(activeQPanel, e.target.value)}
              />

              <div className="cjn-modal-addrow">
                <input
                  type="text"
                  className="cjn-input"
                  placeholder={`Type new ${activeQPanel === "article" ? "article" : activeQPanel === "jobtitle" ? "job title" : "material"} name...`}
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addQEntry(activeQPanel); } }}
                />
                <button
                  type="button"
                  className="cjn-modal-addbtn"
                  disabled={masterLoading[activeQPanel] || !qInput.trim()}
                  onClick={() => addQEntry(activeQPanel)}
                >
                  {masterLoading[activeQPanel] ? "Adding..." : "+ Add Entry"}
                </button>
              </div>

              <div className="cjn-modal-list">
                {(!qdb[activeQPanel] || qdb[activeQPanel].length === 0) ? (
                  <div className="cjn-modal-empty">No database entries found</div>
                ) : (
                  (qSearch[activeQPanel]?.trim()
                    ? qdb[activeQPanel].filter((item) => item.name.toLowerCase().includes(qSearch[activeQPanel].toLowerCase()))
                    : qdb[activeQPanel]
                  ).map((item) => (
                    <div className="cjn-modal-item" key={item.id}>
                      <span className="cjn-modal-item-name">{item.name}</span>
                      <button
                        type="button"
                        className="cjn-modal-delbtn"
                        title="Delete from database"
                        onClick={() => deleteQEntry(activeQPanel, item.id)}
                      >
                        🗑 Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="cjn-modal-ft">
              <button type="button" className="cjn-btn-ghost" onClick={() => setActiveQPanel(null)}>
                Done / Close
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default CreateJobFormNew1;